<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { StateValue } from '@autobid/nuxt-pinia-store/types/Search'
import type { SectionsSearchFormColumnsGroup } from '@autobid/strapi-integration/rest/components'
import type { DictionaryValue } from '@autobid/ui/composables/useDictionary'
import type { FormKitSchemaFormKit } from '@formkit/core'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { useSearchStore } from '@autobid/nuxt-pinia-store/store/searchStore/useSearchStore'
import { usePrefetchDictionary } from '@autobid/ui/composables/useDictionary'
import { getDefaultValuesFromProfile } from '@autobid/ui/composables/useFormKit'
import { useProfile } from '@autobid/ui/composables/useProfile'
import { useSearchCarQuery } from '@autobid/ui/composables/useSearchCarQuery'
import { FormKit } from '@formkit/vue'
import { storeToRefs } from 'pinia'
import { markRaw, onMounted, onServerPrefetch, ref } from 'vue'
import Separator from './base/SearchFormSeparator.vue'
import Apps from './checkbox/SearchFormCheckboxApps.vue'
import CheckboxCustom from './checkbox/SearchFormCheckboxCustom.vue'
import Checkbox from './checkbox/SearchFormCheckboxGroup.vue'
import SearchFormChipsDesktop from './chips/SearchFormChipsDesktop.vue'
import Color from './color/SearchFormColor.vue'
import Eco from './eco/SearchFormEco.vue'
import SearchFormDropdownGroup from './SearchFormDropdownGroup.vue'
import SearchFormGroup from './SearchFormGroup.vue'
import VehicleType from './SearchFormVehicleType.vue'
import Seats from './seat/SearchFormSeats.vue'

const props = defineProps<Props>()

const WITH_AUTH = ['Apps']

const route = useRoute()

const { data: result, isLoading: isResultLoading } = useSearchCarQuery()
const { data: profile } = useProfile()

const prefetchDictionary = usePrefetchDictionary()
const dictionary = await prefetchDictionary()

/**
 * This is just for floating labels, the mutations are triggered through `useFormItem` and `useFormGroupItem`
 */
const modelValue = computed({
  get: () => ({ ...route.query }),
  set: () => null,
})

provide('isResultLoading', isResultLoading)
provide('result', result)

const library = markRaw({
  VehicleType,
  Apps,
  Checkbox,
  Color,
  Seats,
  Eco,
  Separator,
  CheckboxCustom,
})

interface Props {
  data: SectionsSearchFormColumnsGroup
  zIndex: number
}

provide('listButton', props.data.listButton)

const { isAuthed } = useAutobidAuth()

function updateSchemaItemLabel(item: FormKitSchemaFormKit, dictionary: DictionaryValue) {
  if (!item?.$formkit || !dictionary) {
    return item
  }

  const isCustomDictionary = item.dictionary && item.dictionary !== item.name

  const SEARCH_KEY = item.name
    ? `${item.name}${isCustomDictionary ? `-${item.dictionary}` : ''}`
    : item.dictionary

  const labelInDictionary = dictionary[String(item.dictionary)]?.label

  if (
    item.labelAsPlaceholder === false
    && !item['floating-label']
    && !item.floatingLabel
  ) {
    return {
      ...item,
      id: SEARCH_KEY,
      name: SEARCH_KEY,
      label: item.label ?? labelInDictionary,
    }
  }

  if (item.floatingLabel || item['floating-label']) {
    return {
      ...item,
      id: SEARCH_KEY,
      name: SEARCH_KEY,
      placeholder: undefined,
      label: item.label ?? labelInDictionary ?? item.placeholder,
    }
  }

  return {
    ...item,
    id: SEARCH_KEY,
    name: SEARCH_KEY,
    label: undefined,
    placeholder: item.label ?? labelInDictionary ?? item.placeholder,
  }
}

function checkRenderAuth(item: FormKitSchemaFormKit) {
  if (isAuthed.value) {
    return true
  }

  return '$cmp' in item
    && WITH_AUTH.includes(item.$cmp)
    ? isAuthed.value
    : true
}

const router = useRouter()
function updateFormSchema(dictionary: DictionaryValue) {
  if (!props.data.forms) {
    return []
  }

  const parsed = props.data.forms.map(({ formSchema, ...form }) => {
    const updated = (formSchema as FormKitSchemaFormKit[])
      .map(item => updateSchemaItemLabel(item, dictionary))
      .filter(checkRenderAuth)

    return { ...form, formSchema: updated }
  })

  return parsed.filter(item => item.formSchema?.length)
}

const displayingForms = ref<NonNullable<SectionsSearchFormColumnsGroup['forms']>>(updateFormSchema(dictionary))
const registeredForms = ref<typeof displayingForms.value>([])

provide('displayingForms', displayingForms)

if (!displayingForms.value.length && dictionary?.length) {
  displayingForms.value = updateFormSchema(dictionary)
}

onServerPrefetch(async () => {
  const dictionary = await prefetchDictionary()
  if (!Object.keys(dictionary ?? {}).length && displayingForms.value) {
    return
  }
  displayingForms.value = updateFormSchema(dictionary)
})

watch(isAuthed, init)

const {
  chips: storeChips,
  initialQuery,
  allDisplayingForms,
} = storeToRefs(useSearchStore())

async function initializeState(forms: typeof displayingForms.value) {
  const query = await forms.reduce(async (accPromise, form) => {
    const acc = await accPromise
    const formSchema = (form.formSchema || []) as FormKitSchemaFormKit[]

    const result = await Promise.all(
      formSchema.map(async (item) => {
        const queryValue = acc[item.name]
        if (item.value || queryValue) {
          return { [item.name]: queryValue ?? item.value }
        }
        if (item.defaultValue && isAuthed.value) {
          const defaultValueResult = (await getDefaultValuesFromProfile(
            [
              {
                name: item.name,
                defaultValue: item.defaultValue,
                defaultValueProps: item.defaultValueProps,
              },
            ],
            profile,
          )) as Record<string, StateValue>
          return { [item.name]: defaultValueResult[item.name] }
        }

        return {}
      }),
    )

    return Object.assign(acc, ...result)
  }, Promise.resolve({ ...initialQuery.value, ...route.query }))

  initialQuery.value = { ...initialQuery.value, ...query }

  await router.replace({ query: initialQuery.value })
}

const bodyClass = computed(() => {
  const chipsClass = storeChips.value.length ? 'chips' : 'no-chips'

  if (isAuthed.value)
    return `has-search-form-auth ${chipsClass}`

  return `has-search-form ${chipsClass}`
})

useHead({
  bodyAttrs: {
    class: bodyClass,
  },
})

onMounted(init)

function init() {
  if (!process.client) {
    return
  }
  displayingForms.value = updateFormSchema(dictionary)
  allDisplayingForms.value = allDisplayingForms.value.filter(
    form => !registeredForms.value.includes(form),
  )
  registeredForms.value = displayingForms.value
  allDisplayingForms.value = [
    ...allDisplayingForms.value,
    ...registeredForms.value,
  ]
  initializeState(displayingForms.value)
}

onUnmounted(() => {
  allDisplayingForms.value = allDisplayingForms.value.filter(
    form => !registeredForms.value.includes(form),
  )
  initialQuery.value = {}
})
</script>

<template>
  <div
    class="search-form-columns-group relative @container"
    :style="{ zIndex }"
  >
    
<FormKitLazyProvider config-file="true">
<FormKit :model-value="modelValue" type="group">
      <div class="grid grid-cols-12 gap-1 bg-white">
        <component
          :is="form.isDropdown ? SearchFormDropdownGroup : SearchFormGroup"
          v-for="(form, index) in displayingForms"
          :key="form.formSchema.toString()"
          :form="form"
          :library="library"
          :style="{ zIndex: displayingForms.length - index }"
        />
      </div>
    </FormKit>
    <SearchFormChipsDesktop />
</FormKitLazyProvider>

  </div>
</template>

<style lang="scss">
.search-form-columns-group {
  contain: layout;
  will-change: transform;
  .formkit-outer {
    margin-top: 0.5rem;
  }

  .select-options {
    position: fixed; // in order to display dropdown correctly inside a container
  }
}
</style>
